/**
 * Copyright (C) 2021 盈宝信息科技（广州）有限公司 All Rights Reserved.
 */
import config from '@/config'

/**
 * 字符串版本号转数值版本
 * @param {String} version 字符串版本号，如1.6.0
 */
const versionStr2Num = version => {
  if (version) {
    return parseInt(version.split('.').join(''));
  } else {
    return 0;
  }
};

// 比较版本号
const compareVersions=(current, min)=> {
  if (!current || !min) return false // 如果版本号不存在，返回 false

  const currentParts = current.split('.').map(Number)
  const minParts = min.split('.').map(Number)

  // 逐级比较版本号
  for (let i = 0; i < Math.max(currentParts.length, minParts.length); i++) {
    const currentVal = currentParts[i] || 0
    const minVal = minParts[i] || 0

    if (currentVal > minVal) return true // 当前版本更高
    if (currentVal < minVal) return false // 当前版本更低
  }

  return true // 版本完全相同
}

/**
 * 通用app调用配置
 * @param {*} config 调用配置
 */
const callAppMethod = config => {
  // 当不存在webview内，或者全局变量中没注册sdk则退出
  if (!WBAppSdk.inApp || !window[WBAppSdk.name]) return false

  // 注册全局回调函数
  if (config.callback && typeof config.callback === 'function') {
    const callbackName = `__native_callback_${(Math.random() * 100000000).toFixed(0)}`

    const callback = config.callback.bind(config)
    window[callbackName] = function (args) {
      callback(args)
      delete window[callbackName]
    }

    config.callback = callbackName
  }

  window[WBAppSdk.name].postMessage(JSON.stringify(config))
}


/**
 * 在操作前检查app支持的最小版本
 * @param {String} minVersionStr app最低支持版本
 * @param {Function} handle 操作
 */
const checkAppMinVersionBeforeHandle = ({ limitMinVersion, notPassHandle, passHandle }) => {
  // 当不存在webview内，或者全局变量中没注册sdk则退出
  if (!WBAppSdk.inApp) return false;

  if (WBAppSdk.checkAppMinVersion(limitMinVersion)) {
    if (passHandle) {
      passHandle();
    }
  } else {
    if (notPassHandle) {
      notPassHandle();
    }
  }
};

class WBAppSdk {
  static name = 'WBAppSdk'
  static inApp = navigator.userAgent.includes('winBullApp') // 是否在盈宝app内打开
  static version = navigator.userAgent.split('|')[1]; // app版本号
  static baseUrl = config.apiUrl

  /**
   * app弹出媒体选择框
   * @param {String} fileType 文件类型[image/video]
   * @param {Number} uploadType 上传类型[1/2/3/11/12/13/20]
   * 1：大陆身份证前照，2：大陆身份证背照，3：大陆人脸视频，11：香港身份证前照，12：香港身份证背照，13：香港通讯地址，20：转账凭证
   * @param {String} api api接口
   * @param {Function|String} callback 回调函数
   */
  static openMediaSheet(fileType, uploadType, api, callback) {
    return callAppMethod({
      method: 'openMediaSheet',
      params: { fileType, uploadType, httpUrl: WBAppSdk.baseUrl + api },
      callback
    })
  }

  // 检查是否满足app最小版本
  static checkAppMinVersion(limitMinVersion) {
    // 当不存在webview内，或者全局变量中没注册sdk则退出
    if (!WBAppSdk.inApp) return false;
    const appVersion = versionStr2Num(WBAppSdk.version) || 0;
    const minVersion = versionStr2Num(limitMinVersion) || 0;
    return appVersion >= minVersion;
  }

  /**
   * app退出webview
   * @param {Function|String} callback 回调函数
   */
  static exitWebView(callback) {
    return callAppMethod({ method: 'exitWebView', callback })
  }

  /**
   * 从H5接收图片并分享
   * @param {String} img base64图片
   * @param {Function|String} callback 回调函数
   */
  static acceptImgToShare(img, callback) {
    return callAppMethod({ method: 'acceptImgToShare', params: { img: img }, callback })
  }

  /**
   * 行情会员购买
   * @param {String} id 行情会员id
   * @param {Function|String} callback 回调函数
   */
  static buyQuoteVip(id, callback) {
    return callAppMethod({ method: 'buyQuoteVip', params: { id: id }, callback })
  }

  /**
   * app打开客服
   * @param {Function|String} callback 回调函数
   */
  static openOnlineService(callback) {
    return callAppMethod({ method: 'openOnlineService', callback })
  }

  /**
   * app打开第三方共享信息情况说明
   * @param {Function|String} callback 回调函数
   */
  static openThirdPartyShare(callback) {
    return callAppMethod({ method: 'openThirdPartyShare', callback })
  }

  /**
   * app打开隐私政策
   * @param {Function|String} callback 回调函数
   */
  static openPrivacyPolicy(callback) {
    return callAppMethod({ method: 'openPrivacyPolicy', callback })
  }

  /**
   * app打开社区规则
   * @param {Function|String} callback 回调函数
   */
  static openCommunityRule(callback) {
    return callAppMethod({ method: 'openCommunityRule', callback })
  }

  /**
   * app打开指定url
   * @param {String} url 打开的url
   * @param {Function|String} callback 回调函数
   */
  static openWebUrl(url,tips=0, callback) {
    return callAppMethod({ method: 'openWebUrl', params: { url: url,tips:tips }, callback })
  }

  /**
   * 设置WebPage标题
   * @param {String} title web标题
   * @param {Function|String} callback 回调函数
   */
  static setWebTitle(title, callback) {
    return callAppMethod({ method: 'setWebTitle', params: { title: title }, callback })
  }

    /**
   * 活动分享
   */
  static activityLinkShare(params) {
    return callAppMethod({ method: 'activityLinkShare', params })
  }

  /**
 * 获取token
 */
  static getToken(callback) {
    return callAppMethod({ method: 'getToken', callback })
  }

  /**
 * 调起登录
 */
  static toLogin() {
    return callAppMethod({ method: 'login' })
  }

  /**
 * 打开开户活动
 */
  static accountOpenActivityHandle() {
    return callAppMethod({ method: 'accountOpenActivityHandle' })
  }

  /**
 * 打开交易首页
 */
  static gotoTradeHome() {
    return callAppMethod({ method: 'gotoTradeHome' })
  }

  /**
 * 出入金验证交易密码
 */
  static cashHandleVerifyTradePwd(handle, params) {
    checkAppMinVersionBeforeHandle({
      limitMinVersion: '1.9.2', // app最低支持版本【建银需求上线】
      notPassHandle: handle,
      passHandle: function () {
        callAppMethod({ method: 'cashHandleVerifyTradePwd', params });
      },
    });
  }

  /**
   * 打开投资组合首页
   */
  static goPortfolioHome() {
    return callAppMethod({ method: 'goPortfolioHome' })
  }

  /**
   * 打开投资组合详情
   */
  static gotoPortfolioInfo(portfolioCode) {
    return callAppMethod({ method: 'portfolio_fund_detail', params: { portfolioCode: portfolioCode } })
  }

  /**
 * 比较版本号
 */
  static compareVersion(current, min) {
    return compareVersions(current, min)
  }

  /**
   * 股票详情
   */
  static gotoStockDetail(wbCode,callback) {
    return callAppMethod({ method: 'gotoStockDetail', params: { wbCode: wbCode }, callback })
  }

  /**
   * AI策略调用APP手势
   */
  static notifyAiKLineCursor(wbCode,active,callback) {
    return callAppMethod({ method: 'notifyAiKLineCursor', params: { wbCode: wbCode,active:active }, callback })
  }

    /**
   * 去认购Ipo
   */
  static goIpoDetail (ipoId,wbCode,callback) {
    return callAppMethod({ method: 'goIpoDetail', params: { ipoId: ipoId,wbCode:wbCode}, callback })
  }

    /**
   * 修改webview背景色
   * @param {Function|String} callback 回调函数
   */
  static changeWebviewBgc({ bgColor },callback) {
    return callAppMethod({
      method: 'changeWebviewBgc',
      params: { bgColor },
      callback, 
    });
  }
}

export default WBAppSdk
